// Simple Mercator projection tuned for metropolitan France.
// Returns SVG path "d" attribute strings from GeoJSON geometries.
//
// Both axes are normalized to RADIANS before computing a uniform scale,
// so the map preserves the correct aspect ratio at France's latitudes.

const VIEW_W = 1000;
const VIEW_H = 1000;
const LON_MIN = -5;
const LON_MAX = 10;
const LAT_MIN = 41;
const LAT_MAX = 51.5;

const DEG2RAD = Math.PI / 180;

function mercatorY(lat) {
  const rad = lat * DEG2RAD;
  return Math.log(Math.tan(Math.PI / 4 + rad / 2));
}

// Both ranges expressed in radians: lon in plain radians, lat in mercator-y (also radians-like units).
const X_MIN_RAD = LON_MIN * DEG2RAD;
const X_MAX_RAD = LON_MAX * DEG2RAD;
const Y_MIN = mercatorY(LAT_MIN);
const Y_MAX = mercatorY(LAT_MAX);

const X_RANGE = X_MAX_RAD - X_MIN_RAD;
const Y_RANGE = Y_MAX - Y_MIN;

// Uniform scale so the map keeps its correct aspect ratio.
const SCALE = Math.min(VIEW_W / X_RANGE, VIEW_H / Y_RANGE);
const OFFSET_X = (VIEW_W - X_RANGE * SCALE) / 2;
const OFFSET_Y = (VIEW_H - Y_RANGE * SCALE) / 2;

export function project([lon, lat]) {
  const xRad = lon * DEG2RAD;
  const x = (xRad - X_MIN_RAD) * SCALE + OFFSET_X;
  const y = (Y_MAX - mercatorY(lat)) * SCALE + OFFSET_Y;
  return [x, y];
}

function ringToPath(ring) {
  let d = '';
  ring.forEach(([lon, lat], i) => {
    const [x, y] = project([lon, lat]);
    d += (i === 0 ? 'M' : 'L') + x.toFixed(2) + ',' + y.toFixed(2);
  });
  return d + 'Z';
}

export function geometryToPath(geometry) {
  if (!geometry) return '';
  if (geometry.type === 'Polygon') {
    return geometry.coordinates.map(ringToPath).join(' ');
  }
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates
      .map((poly) => poly.map(ringToPath).join(' '))
      .join(' ');
  }
  return '';
}

// Bounding box of a geometry (in projected SVG coords). Used for label placement.
export function projectedBounds(geometry) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const walk = (coords) => {
    coords.forEach((c) => {
      if (typeof c[0] === 'number') {
        const [x, y] = project(c);
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      } else {
        walk(c);
      }
    });
  };
  if (geometry && geometry.coordinates) walk(geometry.coordinates);
  return { minX, minY, maxX, maxY };
}

export const VIEWBOX = `0 0 ${VIEW_W} ${VIEW_H}`;
